import { useState, useEffect, useCallback } from 'react';

const POLL_INTERVAL = 30000; // Check every 30 seconds
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function MaintenanceOverlay() {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [checking, setChecking] = useState(false);
  const [countdown, setCountdown] = useState(30);

  // Listen for maintenance mode events from apiFetch
  useEffect(() => {
    const handleMaintenance = () => setIsMaintenanceMode(true);
    window.addEventListener('rdm:maintenance', handleMaintenance);
    return () => window.removeEventListener('rdm:maintenance', handleMaintenance);
  }, []);

  // Check if maintenance is over.
  //
  // We do a real CORS request to /api/auth/status/. Maintenance is
  // considered over when EITHER:
  //   * status is not 503, OR
  //   * the response has no `X-Maintenance: 1` header
  //
  // On resume, we hide the overlay and dispatch `rdm:maintenance-cleared`
  // so consumers can refetch as needed. We deliberately DO NOT call
  // window.location.reload() — that nuked in-progress visit state
  // (unsaved survey input, drive odometer, etc.) when the false-positive
  // maintenance flicker resolved. IDB persists state across the
  // maintenance window, so a soft resume is sufficient. (#1943)
  const checkStatus = useCallback(async () => {
    setChecking(true);
    try {
      const realCheck = await fetch(`${BASE_URL}/api/auth/status/`, {
        method: 'HEAD',
        cache: 'no-store',
      });
      const stillInMaintenance =
        realCheck.status === 503 && realCheck.headers.get('X-Maintenance') === '1';
      if (!stillInMaintenance) {
        setIsMaintenanceMode(false);
        window.dispatchEvent(new Event('rdm:maintenance-cleared'));
      }
    } catch {
      // Network error or still down — stay in maintenance mode
    } finally {
      setChecking(false);
      setCountdown(30);
    }
  }, []);

  // Auto-poll and countdown timer
  useEffect(() => {
    if (!isMaintenanceMode) return;

    const pollTimer = setInterval(checkStatus, POLL_INTERVAL);
    const countdownTimer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 30));
    }, 1000);

    return () => {
      clearInterval(pollTimer);
      clearInterval(countdownTimer);
    };
  }, [isMaintenanceMode, checkStatus]);

  if (!isMaintenanceMode) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 99999,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        padding: '48px 32px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}>
        {/* Logo */}
        <div style={{ marginBottom: '24px' }}>
          <svg width="180" height="181" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto' }}>
            <path d="M24.5194 13.4429C24.4453 13.9533 24.1087 14.6468 23.7226 15.2497C23.2354 16.0105 22.4622 16.5433 21.5774 16.7283L17.6797 17.5434C17.0533 17.6744 16.4852 18.0022 16.0587 18.479L13.503 21.3357C13.0227 21.8725 12.687 21.7445 12.687 21.0244C12.6812 21.0511 11.4186 24.3072 14.7646 26.2371C16.0502 26.9787 17.9009 26.7122 19.1865 25.9707L25.9983 22.0416C28.5458 20.5722 30.3445 18.0863 30.9424 15.2089C30.966 15.095 30.9843 14.9808 31.004 14.8667L24.5194 13.4429Z" fill="url(#m0)"/>
            <path d="M22.7528 9.51774C24.0384 10.2593 24.5637 11.3633 24.5637 12.8464C24.5637 13.0477 24.5479 13.2466 24.5194 13.4425L27.2641 14.6215L31.004 14.8663C31.4829 12.0948 30.5444 9.24202 28.862 6.97445C27.5959 5.268 25.9667 3.78714 24.0081 2.65738C22.417 1.73966 20.7636 1.13501 19.1025 0.803223L17.2361 3.22023L16.6465 5.99559L22.7528 9.51774Z" fill="url(#m1)"/>
            <path d="M0.783597 11.5518C0.782899 11.554 0.784832 11.5546 0.78556 11.5524C0.929583 11.1205 1.11018 10.6385 1.33564 10.1237C2.51334 7.4343 4.78286 5.64062 7.57492 4.72608C10.367 3.81156 13.4155 4.13212 15.9601 5.59988L16.6465 5.99578L19.1025 0.803412C11.291 -0.756765 3.30728 3.83253 0.793528 11.5217C0.792327 11.5254 0.787957 11.5382 0.783597 11.5518Z" fill="url(#m2)"/>
            <path d="M18.9199 25.9704C17.6343 26.712 16.0503 26.712 14.7647 25.9704C14.5902 25.8697 14.4257 25.7566 14.2701 25.634L12.0091 27.1885L10.0603 30.3376C12.2233 32.1377 15.0321 32.7164 17.839 32.3945C19.9513 32.1523 22.0495 31.4832 24.0082 30.3534C25.5992 29.4357 26.9501 28.3075 28.0682 27.0361L26.9063 24.2128L25.0262 22.4482L18.9199 25.9704Z" fill="url(#m3)"/>
            <path d="M14.2701 25.6341C13.2796 24.8539 12.6872 23.6572 12.6872 22.3754V22.2476V11.5724C12.6872 10.9687 12.865 10.8661 13.3884 11.168C12.5823 10.703 10.7203 9.10701 8.42118 10.4331C7.13557 11.1747 6.0769 12.8116 6.0769 14.2946V22.1529C6.0769 25.0917 7.59906 28.1573 9.79448 30.1133C9.88132 30.1906 9.97122 30.2636 10.0603 30.3377L14.2701 25.6341Z" fill="url(#m4)"/>
            <path d="M27.9105 5.8123C27.909 5.8106 27.9075 5.81197 27.909 5.81368C28.2114 6.15428 28.5389 6.5515 28.8725 7.00399C30.6149 9.36765 31.2659 12.3613 30.6627 15.2343C30.0594 18.1072 28.2573 20.5846 25.7126 22.0523L25.0262 22.4482L28.0683 27.0361C33.3265 21.0576 33.3401 11.8554 27.9316 5.83594C27.9291 5.83306 27.9201 5.82287 27.9105 5.8123Z" fill="url(#m5)"/>
            <path d="M6.34355 14.2944C6.34354 12.8113 7.13552 11.4408 8.42113 10.6993C8.59565 10.5986 8.77601 10.5129 8.96002 10.4395L8.74304 7.70603L7.21862 4.57861C4.57671 5.55005 2.4397 7.55766 1.31528 10.1471C0.469097 12.0957 9.792e-06 14.2458 0 16.5052C0 18.3407 0.302549 20.0735 0.845533 21.6767L3.87391 22.083L6.34355 21.3387V14.2944V14.2944Z" fill="url(#m6)"/>
            <path d="M8.96003 10.4395C10.1316 9.97264 11.4652 10.0584 12.5763 10.6993L12.6871 10.7632L21.5825 15.8941C22.2065 16.254 22.1498 16.6082 21.4445 16.7557L21.9577 16.6484C22.6329 16.5072 23.2498 16.1621 23.7216 15.6592C24.5327 14.7946 24.8305 13.7515 24.8305 12.8463C24.8304 11.3632 24.0385 9.99274 22.7529 9.2512L15.941 5.32209C13.3935 3.85267 10.3394 3.53934 7.5461 4.46083C7.4356 4.49727 7.32744 4.5386 7.21863 4.57861L8.96003 10.4395Z" fill="url(#m7)"/>
            <path d="M19.3222 32.1523C19.3245 32.1518 19.3241 32.1498 19.3218 32.1503C18.8753 32.2417 18.3673 32.3264 17.8083 32.3888C14.8881 32.7145 11.9676 31.781 9.77876 29.8225C7.58999 27.8641 6.3436 25.0662 6.3436 22.1307L6.34359 21.3389L0.845581 21.6769C3.39893 29.2156 11.369 33.8285 19.2912 32.1588C19.295 32.158 19.3083 32.1553 19.3222 32.1523Z" fill="url(#m8)"/>
            <defs>
              <linearGradient id="m0" x1="20.06" y1="24.27" x2="23.21" y2="13.31" gradientUnits="userSpaceOnUse"><stop stopColor="#1724C9"/><stop offset="1" stopColor="#1C64F2"/></linearGradient>
              <linearGradient id="m1" x1="27.31" y1="10.9" x2="19.03" y2="2.65" gradientUnits="userSpaceOnUse"><stop stopColor="#1C64F2"/><stop offset="1" stopColor="#0092FF"/></linearGradient>
              <linearGradient id="m2" x1="16.16" y1="5.52" x2="3.67" y2="6.31" gradientUnits="userSpaceOnUse"><stop stopColor="#0092FF"/><stop offset="1" stopColor="#45B2FF"/></linearGradient>
              <linearGradient id="m3" x1="15.32" y1="29.16" x2="26.54" y2="26.14" gradientUnits="userSpaceOnUse"><stop stopColor="#1C64F2"/><stop offset="1" stopColor="#0092FF"/></linearGradient>
              <linearGradient id="m4" x1="7.27" y1="16.18" x2="15.23" y2="24.43" gradientUnits="userSpaceOnUse"><stop stopColor="#1724C9"/><stop offset="1" stopColor="#1C64F2"/></linearGradient>
              <linearGradient id="m5" x1="25.45" y1="22.14" x2="31.01" y2="10.93" gradientUnits="userSpaceOnUse"><stop stopColor="#0092FF"/><stop offset="1" stopColor="#45B2FF"/></linearGradient>
              <linearGradient id="m6" x1="5.36" y1="9.63" x2="2.39" y2="20.81" gradientUnits="userSpaceOnUse"><stop stopColor="#1C64F2"/><stop offset="1" stopColor="#0092FF"/></linearGradient>
              <linearGradient id="m7" x1="20.54" y1="9.1" x2="9.68" y2="11.8" gradientUnits="userSpaceOnUse"><stop stopColor="#1724C9"/><stop offset="1" stopColor="#1C64F2"/></linearGradient>
              <linearGradient id="m8" x1="6.41" y1="21.86" x2="13.33" y2="32.27" gradientUnits="userSpaceOnUse"><stop stopColor="#0092FF"/><stop offset="1" stopColor="#45B2FF"/></linearGradient>
            </defs>
          </svg>
        </div>

        <h1 style={{ fontSize: '28px', color: '#2d3748', marginBottom: '16px', fontWeight: 700 }}>
          System Maintenance
        </h1>

        <p style={{ fontSize: '16px', color: '#4a5568', marginBottom: '24px', lineHeight: 1.6 }}>
          We're currently performing scheduled maintenance to improve your experience.
        </p>

        <div style={{
          background: '#f7fafc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
        }}>
          <div style={{ fontSize: '12px', color: '#718096', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: 600 }}>
            Current Status
          </div>
          <div style={{ fontSize: '15px', color: '#2d3748', fontWeight: 500 }}>
            {checking ? (
              <>
                <span style={{
                  display: 'inline-block', width: '16px', height: '16px',
                  border: '2px solid #e2e8f0', borderTopColor: '#667eea',
                  borderRadius: '50%', animation: 'maintenance-spin 1s linear infinite',
                  marginRight: '8px', verticalAlign: 'middle',
                }} />
                Checking status...
              </>
            ) : (
              <>
                <span style={{
                  display: 'inline-block', width: '16px', height: '16px',
                  border: '2px solid #e2e8f0', borderTopColor: '#667eea',
                  borderRadius: '50%', animation: 'maintenance-spin 1s linear infinite',
                  marginRight: '8px', verticalAlign: 'middle',
                }} />
                Deploying updates... (checking in {countdown}s)
              </>
            )}
          </div>
        </div>

        <div style={{
          background: '#edf2f7',
          borderLeft: '4px solid #667eea',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '24px',
          textAlign: 'left',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#2d3748', marginBottom: '6px' }}>
            What's happening?
          </div>
          <div style={{ fontSize: '13px', color: '#4a5568', lineHeight: 1.6 }}>
            Our team is deploying the latest updates and improvements.
            This process typically takes 2-5 minutes. The app will automatically
            reload when the update is complete.
          </div>
        </div>

        <button
          onClick={checkStatus}
          disabled={checking}
          style={{
            background: checking ? '#a0aec0' : '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            padding: '12px 28px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: checking ? 'default' : 'pointer',
            width: '100%',
            marginBottom: '16px',
          }}
        >
          {checking ? 'Checking...' : 'Try Again Now'}
        </button>

        <div style={{ fontSize: '13px', color: '#718096' }}>
          <p>Expected return: A few minutes</p>
        </div>
      </div>

      <style>{`
        @keyframes maintenance-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
